import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  supabase,
  getUserSubscription,
  getPlanLimits,
  getRemainingQuota,
  incrementUsage,
  type PlanType,
  type PlanLimits,
} from "@/shared/db";
import type { FeatureType } from "@/shared/core";

export interface UsePlanLimitsResult {
  subscription: {
    planType: PlanType | null;
    planTier: string | null;
    status: string | null;
  } | null;
  limits: PlanLimits | null;
  credits: { balance: number; available: boolean } | null;
  loading: boolean;
  error: Error | null;
  checkLimit: (featureType: FeatureType) => Promise<boolean>;
  getUsage: (featureType: FeatureType) => Promise<number>;
  getRemaining: (featureType: FeatureType) => Promise<number | null>;
  refresh: () => Promise<void>;
  trackUsage: (
    featureType: FeatureType,
    amount?: number,
  ) => Promise<null | undefined>;
}

// Cache to avoid unnecessary reloads
const cache = new Map<
  string,
  {
    subscription: UsePlanLimitsResult["subscription"];
    limits: PlanLimits | null;
    credits: { balance: number; available: boolean } | null;
    timestamp: number;
  }
>();

const CACHE_DURATION = 30000; // 30 seconds

export function usePlanLimits(): UsePlanLimitsResult {
  const { user, profile } = useAuth();
  const [subscription, setSubscription] = useState<{
    planType: PlanType | null;
    planTier: string | null;
    status: string | null;
  } | null>(null);
  const [limits, setLimits] = useState<PlanLimits | null>(null);
  const [credits, setCredits] = useState<{
    balance: number;
    available: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const loadingRef = useRef(false);

  const loadSubscription = useCallback(
    async (force = false) => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      // Check cache first
      const cacheKey = user.id;
      const cached = cache.get(cacheKey);
      const now = Date.now();

      if (!force && cached && now - cached.timestamp < CACHE_DURATION) {
        setSubscription(cached.subscription);
        setLimits(cached.limits);
        setCredits(cached.credits);
        setLoading(false);
        return;
      }

      // Prevent concurrent loads
      if (loadingRef.current) {
        return;
      }

      try {
        loadingRef.current = true;
        setLoading(true);
        setError(null);

        let sub = await getUserSubscription(user.id);

        // Lawyers who completed onboarding should have a subscription.
        // Important: guard against calling ensure_trial when an 'incomplete' row already exists.
        // Otherwise the RPC can try to insert again and trigger 23505.
        if (!sub && profile?.user_type === "lawyer" && profile?.onboarding_completed) {
          const { data: existingAnyStatus } = await supabase
            .from("subscriptions")
            .select("id")
            .eq("user_id", user.id)
            .limit(1)
            .maybeSingle();

          if (!existingAnyStatus) {
          const { error: ensureTrialError } = await supabase.rpc(
            "ensure_trial_for_current_user",
          );
          if (ensureTrialError) {
            console.warn(
              "[usePlanLimits] ensure_trial_for_current_user failed:",
              ensureTrialError.message,
            );
          } else {
            // Small delay to allow DB commit/replication before refetch.
            await new Promise((resolve) => setTimeout(resolve, 150));
            sub = await getUserSubscription(user.id);
          }
          }
        }

        if (sub) {
          const subscriptionData = {
            planType: sub.plan_type,
            planTier: sub.plan_tier,
            status: sub.status,
          };

          setSubscription(subscriptionData);

          // Direct query for plan limits to bypass RPC
          const { data: planLimitsData, error: limitsError } = await supabase
            .from("plan_limits")
            .select("*")
            .eq("plan_type", sub.plan_type)
            .single();

          if (limitsError) {
            console.error("Error loading plan limits directly:", limitsError);
            // Fallback to RPC
            const rpcLimits = await getPlanLimits(sub.plan_type);
            setLimits(rpcLimits);

            // Use rpcLimits for cache
            const planLimits = rpcLimits;

            // Load credits
            try {
              const { getUserCreditsBalance } = await import("@/shared/db");
              const creditsBalance = await getUserCreditsBalance(user.id);

              const creditsData = {
                balance: creditsBalance,
                available: creditsBalance > 0,
              };
              setCredits(creditsData);

              // Update cache
              cache.set(cacheKey, {
                subscription: subscriptionData,
                limits: planLimits,
                credits: creditsData,
                timestamp: now,
              });
            } catch (creditsError) {
              console.error(
                "[usePlanLimits] Error loading credits:",
                creditsError,
              );
              const creditsData = { balance: 0, available: false };
              setCredits(creditsData);
              cache.set(cacheKey, {
                subscription: subscriptionData,
                limits: planLimits,
                credits: creditsData,
                timestamp: now,
              });
            }
          } else {
            const planLimits = planLimitsData as unknown as PlanLimits;
            setLimits(planLimits);

            // Load credits
            try {
              const { getUserCreditsBalance } = await import("@/shared/db");
              const creditsBalance = await getUserCreditsBalance(user.id);

              const creditsData = {
                balance: creditsBalance,
                available: creditsBalance > 0,
              };
              setCredits(creditsData);

              // Update cache
              cache.set(cacheKey, {
                subscription: subscriptionData,
                limits: planLimits,
                credits: creditsData,
                timestamp: now,
              });
            } catch (creditsError) {
              console.error(
                "[usePlanLimits] Error loading credits:",
                creditsError,
              );
              const creditsData = { balance: 0, available: false };
              setCredits(creditsData);
              cache.set(cacheKey, {
                subscription: subscriptionData,
                limits: planLimits,
                credits: creditsData,
                timestamp: now,
              });
            }
          }
        } else {
          // No paid subscription - use free plan limits based on user type
          const freePlanType: PlanType =
            profile?.user_type === "lawyer" || profile?.user_type === "firm"
              ? "lawyer_free"
              : "client_free";

          const subscriptionData = {
            planType: freePlanType,
            planTier: null,
            status: null,
          };
          setSubscription(subscriptionData);

          const { data: planLimitsData, error: limitsError } = await supabase
            .from("plan_limits")
            .select("*")
            .eq("plan_type", freePlanType)
            .single();

          let planLimits: PlanLimits | null = null;
          if (!limitsError && planLimitsData) {
            planLimits = planLimitsData as unknown as PlanLimits;
            setLimits(planLimits);
          } else {
            const rpcLimits = await getPlanLimits(freePlanType);
            planLimits = rpcLimits;
            setLimits(rpcLimits);
          }

          let creditsData = { balance: 0, available: false };
          try {
            const { getUserCreditsBalance } = await import("@/shared/db");
            const creditsBalance = await getUserCreditsBalance(user.id);
            creditsData = {
              balance: creditsBalance,
              available: creditsBalance > 0,
            };
            setCredits(creditsData);
          } catch (creditsError) {
            console.error(
              "[usePlanLimits] Error loading credits (free plan):",
              creditsError
            );
            setCredits(creditsData);
          }

          cache.set(cacheKey, {
            subscription: subscriptionData,
            limits: planLimits,
            credits: creditsData,
            timestamp: now,
          });
        }
      } catch (err) {
        console.error("[usePlanLimits] Error loading subscription:", err);
        setError(err instanceof Error ? err : new Error("Unknown error"));
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    },
    [user?.id, profile?.user_type],
  );

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  const getUsage = useCallback(
    async (featureType: FeatureType): Promise<number> => {
      if (!user?.id) {
        return 0;
      }

      try {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
          .toISOString()
          .split("T")[0];

        // Create a timeout promise
        const timeoutPromise = new Promise<{
          data: null;
          error: { code: string; message: string };
        }>((resolve) => {
          setTimeout(() => {
            console.warn(
              "⚠️ Usage query timed out, failing open (returning 0)",
            );
            resolve({
              data: null,
              error: { code: "TIMEOUT", message: "Query timed out" },
            });
          }, 3000); // 3 second timeout
        });

        // Race the query against the timeout
        const queryPromise = supabase
          .from("usage_tracking")
          .select("usage_count")
          .eq("user_id", user.id)
          .eq("feature_type", featureType)
          .eq("usage_month", startOfMonth)
          .maybeSingle();

        const { data, error } = await Promise.race([
          queryPromise,
          timeoutPromise,
        ]);

        if (error) {
          if (error.code === "PGRST116") {
            // No rows found
            return 0;
          }
          if (error.code === "TIMEOUT") {
            return 0; // Fail open on timeout
          }
          console.error("Error getting usage directly:", error);
          // Fail open on error
          return 0;
        }

        return data?.usage_count || 0;
      } catch (err) {
        console.error("Error getting usage:", err);
        return 0; // Fail open
      }
    },
    [user?.id],
  );

  const checkLimit = useCallback(
    async (featureType: FeatureType): Promise<boolean> => {
      if (!user?.id) {
        return false;
      }

      try {
        // Get subscription and limits directly
        if (!subscription || !limits) {
          return false;
        }

        // Map feature type to limit field
        let limitValue: number | null = null;
        switch (featureType) {
          case "document_generation":
            limitValue = limits.documents_per_month;
            break;
          case "consultation":
            limitValue = limits.consultations_per_month;
            break;
          case "case":
            limitValue = limits.cases_per_month;
            break;
          case "exercise":
            limitValue = limits.exercises_per_month;
            break;
          case "exam":
            limitValue = limits.exams_per_month;
            break;
          case "simulation":
            limitValue = limits.simulations_per_month;
            break;
          case "document_review":
            limitValue = limits.document_reviews_per_month;
            break;
          case "case_summary":
            limitValue = limits.case_summaries_per_month;
            break;
          case "legal_ai_chat":
            limitValue = limits.legal_ai_queries_per_month ?? null;
            break;
        }

        // If limit is null, it means unlimited
        if (limitValue === null) {
          return true;
        }

        // Get current usage directly using our new function
        const currentUsage = await getUsage(featureType);

        // Check if usage is below limit
        const hasAccess = currentUsage < limitValue;

        return hasAccess;
      } catch (err) {
        console.error("❌ Error checking limit:", err);
        return false;
      }
    },
    [user?.id, subscription, limits, getUsage],
  );

  const getRemaining = useCallback(
    async (featureType: FeatureType): Promise<number | null> => {
      if (!user?.id) {
        return null;
      }

      try {
        const result = await getRemainingQuota(user.id, featureType);
        return result;
      } catch (err) {
        console.error("❌ Error getting remaining quota:", err);
        return null;
      }
    },
    [user?.id],
  );

  const refresh = useCallback(async () => {
    if (user?.id) {
      cache.delete(user.id); // Clear cache on refresh
    }
    await loadSubscription(true);
  }, [loadSubscription, user?.id]);

  const trackUsage = useCallback(
    async (featureType: FeatureType, amount: number = 1) => {
      try {
        if (!user?.id) {
          return null;
        }

        await incrementUsage(user.id, featureType, amount);
      } catch (err) {
        console.error("❌ Error tracking usage:", err);
      }
    },
    [user?.id],
  );

  return {
    subscription,
    limits,
    credits,
    loading,
    error,
    checkLimit,
    getUsage,
    getRemaining,
    refresh,
    trackUsage,
  };
}
